import React, { memo, useEffect, useMemo } from "react";
import styles from "./EmploymentInformation.module.scss";
import { CommonSelectItem } from "../../../../common/components/select/CommonSelect";
import { StyleOfFileInput } from "../../../../common/components/file-upload-input/CommonFileUploadInput";
import { useForm } from "react-hook-form";
import { AppDispatch } from "../../../../stores/store";
import { useDispatch, useSelector } from "react-redux";
import { employmentInformationSelector, finishEmploymentInformation } from "../../../../stores/slices/customer-registration-slice";
import { toast } from "react-toastify";
import { showLoader, hideLoader } from "../../../../stores/slices/loader-slice";
import { FileFromObjectUrl } from "../../../../model/common/file-from-object-url";
import CustomerSignUpFormWrapper from "../../../sign-up/wrapper/CustomerSignUpFormWrapper";
import Input from "../../../../common/components/new/ui/Input";
import { BuildingIcon, ContractIcon, LinkedinIcon, SuitcaseIcon, TitleIcon } from "../../../../assets/icons";
import useWindowDimensions, { CurrentScreen } from "../../../../hooks/use-window-dimensions";
import DatePicker from "../../../../common/components/new/datepicker/DatePicker";
import FileUploadInput from "../../../../common/components/new/file-upload/FileUploadInput";
import Select from "../../../../common/components/new/select/Select";

type Props = {
	stepNumber: number;
	onBack: (index: number) => void;
	onNext: (index: number) => void;
};

const EmploymentInformation: React.FC<Props> = memo((props: Props) => {
	const dispatch = useDispatch<AppDispatch>();

	const currentScreen: CurrentScreen = useWindowDimensions();

	const employmentInformation = useSelector(employmentInformationSelector);

	const employmentTypes = useMemo(
		() => [new CommonSelectItem("Full Time", "Full Time"), new CommonSelectItem("Part Time", "Part Time"), new CommonSelectItem("Contract", "Contract")],
		[]
	);

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		control,
		formState: { errors, isValid, isSubmitted },
	} = useForm<{
		companyName: string;
		jobTitle: string;
		joinDate: Date;
		employmentType?: CommonSelectItem;
		employmentContract?: FileFromObjectUrl;
		linkedInPage?: string;
	}>({
		reValidateMode: "onSubmit",
		mode: "onBlur",
	});

	const onSubmit = async () => {
		try {
			dispatch(showLoader());

			const result = await dispatch(
				finishEmploymentInformation({
					companyName: watch("companyName"),
					jobTitle: watch("jobTitle"),
					employmentContract: watch("employmentContract")!,
					employmentType: watch("employmentType")?.key,
					joinDate: watch("joinDate"),
					linkedInPage: watch("linkedInPage"),
				})
			);

			if (finishEmploymentInformation.fulfilled.match(result)) {
				toast.success("Employment information saved successfully");
				props.onNext(props.stepNumber + 1);
			} else if (finishEmploymentInformation.rejected.match(result)) {
				toast.error(result.payload as string);
			}
		} finally {
			dispatch(hideLoader());
		}
	};

	const initialEmploymentContact = () => {
		return watch("employmentContract") !== undefined ? [watch("employmentContract")!.name] : [];
	};

	const onFilesSelection = (file: File[]) => {
		const files: FileFromObjectUrl[] = [];

		file.forEach((f: File) => {
			files.push({
				name: f.name,
				url: URL.createObjectURL(f),
			});
		});

		setValue("employmentContract", files[0], { shouldValidate: true, shouldTouch: true, shouldDirty: true });
	};

	useEffect(() => {
		setValue("companyName", employmentInformation.companyName ?? "");
		setValue("jobTitle", employmentInformation.jobTitle ?? "");
		setValue("joinDate", new Date(employmentInformation.dateOfEmployment ?? new Date().toISOString()));
		setValue("employmentType", employmentTypes.find(type => type.key === employmentInformation.employmentType) ?? employmentTypes[0]);
		setValue("employmentContract", employmentInformation.employmentContractFile);
		setValue("linkedInPage", employmentInformation.linkedInProfileUrl ?? "");
	}, [employmentInformation, employmentTypes, setValue]);

	const registeredInputs = useMemo(() => {
		return {
			companyName: register("companyName", { required: "Comapny name is required" }),
			jobTitle: register("jobTitle", { required: "Job title is required" }),
			joinDate: register("joinDate", { required: "Join date is required" }),
			employmentContract: register("employmentContract", { required: "Employment contract is required" }),
			linkedInPage: register("linkedInPage"),
		};
	}, [register, watch]);

	return (
		<CustomerSignUpFormWrapper
			onBack={() => props.onBack(props.stepNumber - 1)}
			onNext={onSubmit}
			nextButtonDisabled={!isValid}
			labelBackStep="Personal"
			labelNextStep="Identity"
			title="Verification Information"
			subtitle="Verify Employment Information"
		>
            <div className={styles.container}>
            {
                        !currentScreen.isMobile ?
                        <div className={`formFieldInlineContainer ${styles.formInlineRow}`}>
                            <Input
                                ref={registeredInputs.companyName.ref}
                                label="Company Name"
                                required
                                placeholder="e.g sample company limited"
                                error={errors.companyName?.message}
                                inputProps={registeredInputs.companyName}
                            >
                                <BuildingIcon />
                            </Input>
                            <Input
                                ref={registeredInputs.jobTitle.ref}
                                label="Job Title"
                                required
                                placeholder="e.g Lines manager 1"
                                error={errors.jobTitle?.message}
                                inputProps={registeredInputs.jobTitle}
                            >
                                <TitleIcon />
                            </Input>
                        </div>
                        :
                        <>
                            <Input
                                ref={registeredInputs.companyName.ref}
                                label="Company Name"
                                required
                                placeholder="e.g sample company limited"
                                error={errors.companyName?.message}
                                inputProps={registeredInputs.companyName}
                            >
                                <BuildingIcon />
                            </Input>
                            <Input
                                ref={registeredInputs.jobTitle.ref}
                                label="Job Title"
                                required
                                placeholder="e.g Lines manager 1"
                                error={errors.jobTitle?.message}
                                inputProps={registeredInputs.jobTitle}
                            >
                                <TitleIcon />
                            </Input>
                        </>
                    }
                <DatePicker 
                    initialDate={watch('joinDate')} 
                    label='When did you join the company?'
                    required
                    error={errors.joinDate?.message}
                    onChange={date => setValue('joinDate', date, { shouldTouch: true})} />
                <Select
                    label="Employment Type"
                    placeholder="Select"
                    required
                    error={errors.employmentType?.message}
                    items={employmentTypes}
                    onChange={value => setValue('employmentType', value)}
                >
                    <SuitcaseIcon />
                </Select>
                <FileUploadInput
                    label='Employment Contract'
                    maxFileSize={24}
                    accept='image/*'
                    required
                    error={errors.employmentContract?.message}
                    styleOfInput={StyleOfFileInput.FormInput}
                    registerInputProps={registeredInputs.employmentContract}
                    onFilesSelect={onFilesSelection}
                >
                    <ContractIcon />
                </FileUploadInput>
                <Input
                    ref={registeredInputs.linkedInPage.ref}
                    label="LinkedIn Page"
                    placeholder="optional"
                    error={errors.linkedInPage?.message}
                    inputProps={registeredInputs.linkedInPage}
                >
                    <LinkedinIcon />
                </Input>
            </div>
        </CustomerSignUpFormWrapper>
    )
})

export default EmploymentInformation;
